import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Auth } from '../auth/auth';
import { environment } from '../../../environments/environment';

export interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

export interface ChatMessage {
  id: string;
  conversation_id?: string;
  content: string;
  sender_id: string;
  receiver_id?: string | null;
  sender_name: string;
  sender_avatar?: string;
  timestamp: string;
  message_type: string;
  is_read: boolean;
  read_by_count?: number;
  embed?: any;
}

export interface UserStatus {
  user_id: number;
  profile_id: number;
  display_name: string;
  status: 'online' | 'away' | 'busy' | 'offline';
  last_seen?: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private auth = inject(Auth);
  private chatSocket: WebSocket | null = null;
  private globalChatSocket: WebSocket | null = null;
  private statusSocket: WebSocket | null = null;
  private heartbeatInterval: any = null;
  /** Pending `scheduleReconnect()` timer — tracked so ngOnDestroy can cancel
   * it. Left unguarded, this setTimeout survives Angular's own teardown
   * (it's a bare native timer, not an Angular resource) and its
   * console.log calls can fire well after this service instance is gone —
   * in specs, after Vitest has moved on to a later test file, which
   * surfaces as an "onUserConsoleLog was pending" teardown error against
   * whatever happens to be running at that moment. */
  private reconnectTimeout: any = null;
  private isInitialized = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  /** Set when we close the global chat socket to replace it; prevents onclose from scheduling a reconnect. */
  private globalChatIntentionalClose = false;
  /** True once ngOnDestroy has run — guards every socket callback and the
   * reconnect scheduler so nothing keeps talking (or logging) after this
   * service should be gone. */
  private destroyed = false;

  // Signals for reactive state management
  isConnected = signal(false);
  userStatuses = signal<UserStatus[]>([]);
  typingUsers = signal<Set<string>>(new Set());

  constructor() {
    // Only connect if user is authenticated and profile is selected
    if (typeof window !== 'undefined' && this.auth.getAccessToken() && this.auth.getSelectedProfileId() && !this.isInitialized) {
      this.connectStatusSocket();
      this.connectGlobalChatSocket();
      this.startHeartbeat();
      this.isInitialized = true;
    }
  }

  initialize(): void {
    // Initialize WebSocket connections if not already done
    if (typeof window !== 'undefined' && this.auth.getAccessToken() && this.auth.getSelectedProfileId() && !this.isInitialized) {
      console.log('WebSocketService: Initializing connections...');
      this.connectStatusSocket();
      this.connectGlobalChatSocket();
      this.startHeartbeat();
      this.isInitialized = true;
    }
  }

  disconnect(): void {
    console.log('WebSocketService: Disconnecting all connections...');
    
    if (this.chatSocket) {
      this.chatSocket.close();
      this.chatSocket = null;
    }
    
    if (this.globalChatSocket) {
      this.globalChatSocket.close();
      this.globalChatSocket = null;
    }
    
    if (this.statusSocket) {
      this.statusSocket.close();
      this.statusSocket = null;
    }
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.isConnected.set(false);
    this.isInitialized = false;
  }

  /** Angular calls this automatically when the providing injector is torn
   * down (route/lazy-module destroy in prod; between-test TestBed reset in
   * specs). See the `destroyed` flag doc comment above for why this matters
   * beyond just closing sockets. */
  ngOnDestroy(): void {
    this.destroyed = true;
    this.disconnect();
  }

  reconnectForProfileChange(): void {
    // Disconnect existing connections
    this.disconnect();
    
    // Reconnect with new profile
    this.connectStatusSocket();
    this.connectGlobalChatSocket();
    
    // Note: Chat socket will be reconnected when a conversation is selected
    console.log('WebSocket reconnected for profile change');
  }

  connectGlobalChatSocket(): void {
    if (this.globalChatSocket) {
      this.globalChatIntentionalClose = true;
      this.globalChatSocket.close();
      this.globalChatSocket = null;
    }

    const token = this.auth.getAccessToken();
    if (!token) {
      console.error('No access token available for global chat WebSocket connection');
      return;
    }

    const selectedProfileId = this.auth.getSelectedProfileId();
    if (!selectedProfileId) {
      console.error('No selected profile for global chat WebSocket connection');
      return;
    }

    // Connect to a global chat channel for receiving new conversations
    const wsUrl = `${this.getWebsocketBase()}/ws/chat/global/?token=${token}&profile_id=${selectedProfileId}`;
    
    this.globalChatSocket = new WebSocket(wsUrl);
    
    this.globalChatSocket.onopen = () => {
      if (this.destroyed) return;
      console.log('Global Chat WebSocket connected');
      this.isConnected.set(true);
      this.resetReconnectState();
      window.dispatchEvent(new CustomEvent('websocketConnected'));
    };

    this.globalChatSocket.onmessage = (event) => {
      if (this.destroyed) return;
      const data = JSON.parse(event.data);
      this.handleGlobalChatMessage(data);
    };

    this.globalChatSocket.onclose = () => {
      if (this.globalChatIntentionalClose) {
        this.globalChatIntentionalClose = false;
        return;
      }
      this.globalChatSocket = null;
      if (this.destroyed) return;
      console.log('Global Chat WebSocket disconnected');
      this.isConnected.set(false);
      window.dispatchEvent(new CustomEvent('websocketDisconnected'));
      this.scheduleReconnect();
    };

    this.globalChatSocket.onerror = (error) => {
      if (this.destroyed) return;
      console.error('Global Chat WebSocket error:', error);
    };
  }

  connectChatSocket(conversationId: string): void {
    if (this.chatSocket) {
      this.chatSocket.close();
    }

    const token = this.auth.getAccessToken();
    if (!token) {
      console.error('No access token available for WebSocket connection');
      return;
    }

    const selectedProfileId = this.auth.getSelectedProfileId();
    if (!selectedProfileId) {
      console.error('No selected profile for WebSocket connection');
      return;
    }

    const wsUrl = `${this.getWebsocketBase()}/ws/chat/${conversationId}/?token=${token}&profile_id=${selectedProfileId}`;
    
    this.chatSocket = new WebSocket(wsUrl);
    
    this.chatSocket.onopen = () => {
      if (this.destroyed) return;
      console.log('Chat WebSocket connected');
    };

    this.chatSocket.onmessage = (event) => {
      if (this.destroyed) return;
      const data = JSON.parse(event.data);
      this.handleChatMessage(data);
    };

    this.chatSocket.onclose = () => {
      if (this.destroyed) return;
      console.log('Chat WebSocket disconnected');
    };

    this.chatSocket.onerror = (error) => {
      if (this.destroyed) return;
      console.error('Chat WebSocket error:', error);
    };
  }

  connectStatusSocket(): void {
    if (this.statusSocket) {
      this.statusSocket.close();
    }

    const token = this.auth.getAccessToken();
    if (!token) {
      console.error('No access token available for WebSocket connection');
      return;
    }

    const selectedProfileId = this.auth.getSelectedProfileId();
    if (!selectedProfileId) {
      console.error('No selected profile for WebSocket connection');
      return;
    }

    const wsUrl = `${this.getWebsocketBase()}/ws/status/?token=${token}&profile_id=${selectedProfileId}`;
    
    this.statusSocket = new WebSocket(wsUrl);
    
    this.statusSocket.onopen = () => {
      if (this.destroyed) return;
      console.log('Status WebSocket connected');
      this.isConnected.set(true);
    };

    this.statusSocket.onmessage = (event) => {
      if (this.destroyed) return;
      const data = JSON.parse(event.data);
      this.handleStatusMessage(data);
    };

    this.statusSocket.onclose = () => {
      if (this.destroyed) return;
      console.log('Status WebSocket disconnected');
      this.isConnected.set(false);
    };

    this.statusSocket.onerror = (error) => {
      if (this.destroyed) return;
      console.error('Status WebSocket error:', error);
    };
  }

  private getWebsocketBase(): string {
    const apiBase = environment.apiBaseUrl || '';
    // When apiBaseUrl is relative (e.g. /api), use current page origin
    if (typeof window !== 'undefined' && (apiBase.startsWith('/') || !apiBase.match(/^https?:\/\//))) {
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      return `${protocol}://${window.location.host}`;
    }
    const configuredApiBase = apiBase.replace(/\/?api\/?$/, '');
    const isHttps = configuredApiBase.startsWith('https');
    const scheme = isHttps ? 'wss' : 'ws';
    const host = configuredApiBase.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    return host ? `${scheme}://${host}` : (typeof window !== 'undefined' ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}` : 'ws://localhost:4000');
  }

  private handleGlobalChatMessage(data: WebSocketMessage): void {
    switch (data.type) {
      case 'new_conversation':
        // Emit custom event for new conversations
        console.log('Global Chat: Received new conversation', data['conversation']);
        window.dispatchEvent(new CustomEvent('newConversation', { detail: data['conversation'] }));
        break;
      case 'conversation_updated':
        // Emit custom event for conversation updates
        console.log('Global Chat: Received conversation update', data['conversation']);
        window.dispatchEvent(new CustomEvent('conversationUpdated', { detail: data['conversation'] }));
        break;
    }
  }

  private handleChatMessage(data: WebSocketMessage): void {
    switch (data.type) {
      case 'chat_message':
        // Emit custom event for chat messages
        window.dispatchEvent(new CustomEvent('chatMessage', { detail: data['message'] }));
        break;
      case 'new_conversation':
        // Emit custom event for new conversations
        window.dispatchEvent(new CustomEvent('newConversation', { detail: data['conversation'] }));
        break;
      case 'typing':
        // Handle typing indicators
        if (data['is_typing']) {
          this.typingUsers.update(users => new Set([...users, data['user_name']]));
        } else {
          this.typingUsers.update(users => {
            const newSet = new Set(users);
            newSet.delete(data['user_name']);
            return newSet;
          });
        }
        break;
    }
  }

  private handleStatusMessage(data: WebSocketMessage): void {
    switch (data.type) {
      case 'all_status':
        this.userStatuses.set(data['statuses']);
        break;
      case 'status_update':
        this.userStatuses.update(statuses => {
          const existing = statuses.find(status => status.user_id === data['user_id']);
          if (!existing) {
            return [...statuses, {
              user_id: data['user_id'],
              profile_id: data['profile_id'],
              display_name: data['display_name'] || '',
              status: data['status'],
              last_seen: data['last_seen']
            }];
          }
          return statuses.map(status =>
            status.user_id === data['user_id']
              ? { ...status, status: data['status'] }
              : status
          );
        });
        break;
    }
  }

  sendChatMessage(content: string): void {
    if (this.chatSocket && this.chatSocket.readyState === WebSocket.OPEN) {
      this.chatSocket.send(JSON.stringify({
        type: 'chat_message',
        content: content
      }));
    }
  }

  sendTypingStatus(isTyping: boolean, userName: string): void {
    if (this.chatSocket && this.chatSocket.readyState === WebSocket.OPEN) {
      this.chatSocket.send(JSON.stringify({
        type: 'typing',
        is_typing: isTyping,
        user_name: userName
      }));
    }
  }

  updateUserStatus(status: 'online' | 'away' | 'busy' | 'offline', isVisible: boolean = true): void {
    if (this.statusSocket && this.statusSocket.readyState === WebSocket.OPEN) {
      this.statusSocket.send(JSON.stringify({
        type: 'status_update',
        status: status,
        is_visible: isVisible
      }));
    }
  }

  private startHeartbeat(): void {
    // Send heartbeat every 60 seconds
    this.heartbeatInterval = setInterval(() => {
      if (this.statusSocket && this.statusSocket.readyState === WebSocket.OPEN) {
        this.statusSocket.send(JSON.stringify({
          type: 'heartbeat'
        }));
      }
      
      if (this.globalChatSocket && this.globalChatSocket.readyState === WebSocket.OPEN) {
        this.globalChatSocket.send(JSON.stringify({
          type: 'heartbeat'
        }));
      }
      
      if (this.chatSocket && this.chatSocket.readyState === WebSocket.OPEN) {
        this.chatSocket.send(JSON.stringify({
          type: 'heartbeat'
        }));
      }
    }, 60000); // 60 seconds
  }


  getUserStatus(userId: number): 'online' | 'away' | 'busy' | 'offline' {
    const statuses = this.userStatuses();
    const userStatus = statuses.find(s => s.user_id === userId);
    return userStatus?.status || 'offline';
  }

  getProfileStatus(profileId: number): 'online' | 'away' | 'busy' | 'offline' {
    const statuses = this.userStatuses();
    const userStatus = statuses.find(s => s.profile_id === profileId);
    return userStatus?.status || 'offline';
  }

  private scheduleReconnect(): void {
    if (this.destroyed) return;

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached. Stopping reconnection.');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff

    console.log(`Scheduling reconnection attempt ${this.reconnectAttempts} in ${delay}ms`);

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      if (this.destroyed) return;
      if (typeof window !== 'undefined' && this.auth.getAccessToken()) {
        console.log(`Attempting reconnection ${this.reconnectAttempts}`);
        this.connectGlobalChatSocket();
        this.connectStatusSocket();
      }
    }, delay);
  }

  private resetReconnectState(): void {
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;
  }
}
